<script setup>
/**
 * TableRow: the top pane folded to the table (2026-10-21 talk card design,
 * "The top pane: it folds to the table row"; 2026-10-22 gift redesign §8).
 * The reading's title on one line, the spread as mini cards (Past · Present ·
 * Future · Advice), a small gold ✦, then each pulled card as a tile labelled
 * "Pulled" (or "Pulled 1" / "Pulled 2"; "Yours" in a gift room). The newest
 * pulled tile glows gold once. Any tile opens the pane on that card.
 */
const props = defineProps({
  title: { type: String, default: '' },
  // [{ position, name, image, reversed }]
  cards: { type: Array, default: () => [] },
  // [{ label, name, image, reversed }]
  pulled: { type: Array, default: () => [] },
})
const emit = defineEmits(['open'])
const short = (p) => String(p || '').replace(/^the\s+/i, '')
const newest = () => props.pulled.length - 1
</script>

<template>
  <section class="tr" data-testid="table-row" aria-label="The table">
    <h2 v-if="title" class="tr-title">{{ title }}</h2>
    <div class="tr-row">
      <button v-for="(c, i) in cards" :key="'c' + i" type="button" class="tr-tile"
              :aria-label="`${short(c.position)}: ${c.name}`" :data-testid="`table-tile-${i}`"
              @click="emit('open', { kind: 'card', index: i })">
        <span class="tr-art"><img v-if="c.image" :src="c.image" :alt="c.name" :class="{ rev: c.reversed }" /></span>
        <span class="tr-label">{{ short(c.position) }}</span>
      </button>
      <span v-if="pulled.length" class="tr-star" aria-hidden="true">✦</span>
      <button v-for="(p, k) in pulled" :key="'p' + k" type="button" class="tr-tile tr-pulled"
              :class="{ 'is-new': k === newest() }" :aria-label="`${p.label}: ${p.name}`"
              data-testid="table-tile-pulled" @click="emit('open', { kind: 'pulled', index: k })">
        <span class="tr-art"><img v-if="p.image" :src="p.image" :alt="p.name" :class="{ rev: p.reversed }" /></span>
        <span class="tr-label">{{ p.label }}</span>
      </button>
    </div>
  </section>
</template>

<style scoped>
.tr {
  --display: 'Letter Display', 'Cinzel', Georgia, serif;
  /* The foot clears the divider's handle, which sits on the pane's edge
     (it sat on the labels, gift bug walk 2026-10-25 #29). */
  box-sizing: border-box; padding: 12px 12px 22px; text-align: center;
  background: linear-gradient(180deg, #fdfaf2, #f6eedd); border-radius: 0 0 14px 14px;
}
.tr-title { margin: 0 0 8px; font: 600 15px/1.25 var(--display); color: #2a1845; letter-spacing: 0.02em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding: 0 40px; }
.tr-row { display: flex; align-items: flex-start; justify-content: center; gap: clamp(8px, 3vw, 22px); }
.tr-tile { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 0; border: 0; background: none; cursor: pointer; min-width: 44px; }
.tr-art { display: block; width: 34px; height: 52px; border-radius: 5px; overflow: hidden; border: 1px solid #b8884a;
  background: #2a1845; box-shadow: 0 6px 12px -8px rgba(42, 24, 69, 0.8); }
.tr-art img { display: block; width: 100%; height: 100%; object-fit: cover; }
.tr-art img.rev { transform: rotate(180deg); }
.tr-label { font: 600 9.5px/1.2 var(--display); letter-spacing: 0.14em; text-transform: uppercase; color: #6b5a82; white-space: nowrap; }
.tr-star { align-self: center; margin-top: -14px; color: #c9a94e; font-size: 12px; }
.tr-pulled .tr-art { border-color: #c9a94e; box-shadow: 0 0 0 3px rgba(240, 217, 163, 0.55); }
.tr-pulled .tr-label { color: #8a6a2a; }
.tr-pulled.is-new .tr-art { animation: tr-glow 1.6s ease-out 1; }
@keyframes tr-glow {
  0% { box-shadow: 0 0 0 3px rgba(240, 217, 163, 0.55); }
  35% { box-shadow: 0 0 0 6px rgba(240, 217, 163, 0.9), 0 0 22px 6px rgba(240, 200, 110, 0.75); }
  100% { box-shadow: 0 0 0 3px rgba(240, 217, 163, 0.55); }
}
.tr-tile:focus-visible { outline: 2px solid #7b4bb0; outline-offset: 3px; border-radius: 6px; }
/* Phones: the account portrait (fixed, top-right) sits over the pane's top
   band; the title keeps clear of it, the same on both sides so it stays
   centred (it kept 72px on the left for the old balance badge). */
@media (max-width: 699px) {
  .tr-title { padding: 0 var(--tra-clear-right, 64px); font-size: 14px; }
  /* The tiles keep clear of it too: with five tiles the last one ran under
     it (gift bug walk 2026-10-25 #31). */
  .tr-row { box-sizing: border-box; padding: 0 calc(var(--tra-clear-right, 64px) - 8px); gap: clamp(6px, 2.5vw, 22px); }
  /* Each tile keeps to its own column and long positions wrap inside it:
     nowrap labels ran into each other at 390 ("SURFACERUTH BENEATHDECIPHER",
     loop walk 2026-10-26 #3). */
  /* …but only between words: "anywhere" broke PRESENT into "PRESEN/T"
     (lesson draw walk 2 #2). A tile is at least as wide as its longest word. */
  .tr-tile { flex: 0 1 52px; min-width: 38px; min-width: min-content; }
  .tr-label { white-space: normal; font-size: 8.5px; line-height: 1.15; letter-spacing: 0.04em;
    max-width: 100%; overflow-wrap: normal; word-break: normal; hyphens: none; text-wrap: balance; }
}
@media (min-width: 700px) {
  .tr { padding: 14px 16px 24px; }
  .tr-title { font-size: 20px; margin-bottom: 10px; }
  .tr-art { width: 46px; height: 72px; }
  .tr-label { font-size: 10px; }
}
@media (prefers-reduced-motion: reduce) { .tr-pulled.is-new .tr-art { animation: none; } }
</style>
